/**
 * @module Views/MyWill/WillGenerationChecklist
 *
 * @description This module defines the `WillGenerationChecklist` component, which handles the checklist process for generating a will.
 * The component includes multi-step logic for verifying that all required information is provided, such as personal details, assets, distribution, and people involved (beneficiaries, executors, etc.).
 * It also handles the submission and preview of a will, utilizing APIs to fetch and save data.
 *
 * @example
 * // Example usage of WillGenerationChecklist component
 * <WillGenerationChecklist setHtml={setHtmlFunction} />
 */
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  Grid,
  IconButton,
  Step,
  StepConnector,
  StepContent,
  StepLabel,
  Stepper,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { ContextProvider } from 'App';
import { CustomAutocomplete } from 'components/DesignSystem/AutoComplete/CustomAutocomplete';
import { ContainedButton1 } from 'components/DesignSystem/Button';
import Icon from 'components/DesignSystem/Icon';
import InputField from 'components/DesignSystem/InputField';
import PhoneField from 'components/PhoneField';
import { useFormik } from 'formik';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateWillApi, previewDraftApi } from 'services/myWillService';
import { BankList } from 'utils/helper/BankList';
import formValidation from 'utils/helper/formValidations';
import * as Yup from 'yup';

/**
 * Custom Input Field Component
 *
 * This component is a wrapper around the `InputField` component, adding custom styles
 * and accepting a `placeholder`, `name`, `value`, `onChange`, and additional props.
 * It allows for custom styling and includes modifications like adjusting the height of the input field
 * and styling the placeholder text.
 *
 * @component
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.placeholder - The placeholder text to display in the input field.
 * @param {string} props.name - The name attribute of the input field, used for form handling.
 * @param {string} props.value - The value of the input field, usually controlled via state.
 * @param {function} props.onChange - The function that is called when the value of the input field changes.
 * @param {Object} [props.sx] - Optional styles to override the default input styling.
 * @param {Object} [props] - Any additional props to pass down to the `InputField` component.
 *
 * @returns {React.Element} - The rendered custom input field component.
 */
const CustomInputField = ({ placeholder, name, value, onChange, sx, ...props }) => {
  return (
    <InputField
      placeholder={placeholder}
      name={name}
      value={value}
      onChange={onChange}
      sx={{
        '& .MuiInputBase-root': {
          height: '30px',
        },
        '& .MuiInputBase-input::placeholder': {
          fontSize: '14px',
        },
        ...sx,
      }}
      {...props}
    />
  );
};

/**
 * Custom Step Icon Component
 *
 * This component represents a step icon for a stepper component. It customizes the appearance
 * of the step icon based on whether the step is active, completed, or not.
 * The icon's background color, border color, and text color change based on the step's state.
 *
 * @component
 *
 * @param {Object} props - The properties passed to the component.
 * @param {boolean} props.active - Determines if the step is currently active.
 * @param {boolean} props.completed - Determines if the step is completed.
 * @param {React.Node} props.icon - The icon to display within the step circle.
 *
 * @returns {React.Element} - The rendered step icon with dynamic styles.
 */
const CustomStepIcon = ({ active, completed, icon }) => {
  const theme = useTheme();
  const color = completed || active ? '#FFF3EA' : theme.palette.grey[200];
  const border = completed || active ? '1px solid #FFA25C' : `1px solid ${theme.palette.grey[100]}`;
  const textColor = completed || active ? '#F47B20' : theme.palette.grey[100];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 33,
        height: 33,
        borderRadius: '50%',
        border,
        backgroundColor: color,
      }}
    >
      <Typography color={textColor} variant="bodyText2">
        {icon}
      </Typography>
    </Box>
  );
};

/**
 * Custom Inner Step Icon Component
 *
 * This component represents an inner step icon used in the stepper. It changes the icon
 * based on the completion status of the step (checked or unchecked).
 *
 * @component
 *
 * @param {Object} props - The properties passed to the component.
 * @param {boolean} props.active - Determines if the step is active.
 * @param {boolean} props.completed - Determines if the step is completed.
 *
 * @returns {React.Element} - The rendered inner step icon, which changes based on the step's state.
 */
const CustomInnerStepIcon = (props) => {
  const { active, completed } = props;
  return (
    <Box sx={{ marginLeft: '5px' }}>
      {completed ? (
        <Icon name="stepper-checked-orange.png" size={15} />
      ) : (
        <Icon name="stepper-unchecked-orange.png" size={15} />
      )}
    </Box>
  );
};

/**
 * Custom Step Connector Component
 *
 * This component is a styled version of the `StepConnector` component that is used
 * to visually connect steps in a stepper. The connector color changes depending on the state
 * of the step (active, completed, or default).
 *
 * @component
 *
 * @returns {React.Element} - The rendered custom step connector with dynamic color styling.
 */
const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderColor: '#FFD5B5', // Default color for remaining steps
  },
  '&.Mui-active .MuiStepConnector-line': {
    borderColor: theme.palette.primary.main, // Color for the active step
  },
  '&.Mui-completed .MuiStepConnector-line': {
    borderColor: theme.palette.primary.main, // Color for completed steps
  },
}));

/**
 * Custom Inner Step Connector Component
 *
 * This component is a styled version of the `StepConnector` component with a dashed line
 * connecting the steps. The dashed line color and style change based on whether the step is active or completed.
 *
 * @component
 *
 * @returns {React.Element} - The rendered custom inner step connector with dashed line style.
 */
const CustomInnerStepConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderLeft: `1px dashed ${theme.palette.primary.light}`,
  },
  '&.Mui-active .MuiStepConnector-line': {
    borderLeft: `1px dashed ${theme.palette.primary.main}`,
  },
  '&.Mui-completed .MuiStepConnector-line': {
    borderLeft: `1px dashed ${theme.palette.primary.main}`,
  },
}));

/**
 * WillGenerationChecklist Component
 *
 * This component provides a checklist interface for users to generate a will. It uses a multi-step form process to collect and validate information such as:
 * - Executor details
 * - Wealth manager details
 * - Bank locker information
 * - Beneficiaries, executors, and assets
 *
 * The component includes dynamic form fields, validations, and conditional steps to ensure all required information is provided before proceeding.
 *
 * @component
 * @param {Object} props - The properties passed to the `WillGenerationChecklist` component.
 * @param {function} props.setHtml - A function to set the HTML content for the will document preview.
 *
 * @returns {React.Element} - The rendered component which includes a multi-step checklist form, showing the user’s progress and collecting necessary data.
 */
const WillGenerationChecklist = ({ setHtml }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [step1Completed, setStep1Completed] = useState([false, false, false, false]);
  const [resp, setResp] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { user, fetchUser } = useContext(ContextProvider);

  // Initial form values
  const initialValues = {
    executorHasCopy: false,
    copyWithPerson: false,
    copyInBankLocker: false,
    personName: '',
    location: '',
    bankName: '',
    branchName: '',
    bankLockerNo: '',
    branchAddress: '',
    wealthManager: false,
    wealthManagers: [{}],
  };

  // Formik setup with initial values, validation, and submit handler
  const formik = useFormik({
    initialValues,
    validate: (values) => {
      const schema = Yup.object({
        ...(values.copyWithPerson && {
          personName: formValidation.inputTextValidation.required('Required'),
          location: formValidation.inputTextValidation.required('Required'),
        }),
        ...(values.copyInBankLocker && {
          bankName: formValidation.inputTextValidation.required('Required field'),
          branchName: formValidation.inputTextValidation.required('Required field'),
          bankLockerNo: formValidation.bankLockerValidation.required('Required field'),
          branchAddress: Yup.string().required('Required'),
        }),
        ...(values.wealthManager && {
          wealthManagers: Yup.array().of(
            Yup.object().shape({
              name: formValidation.inputTextValidation.required('Required field'),
              clientId: formValidation.clientIdValidation.required('Required field'),
              email: formValidation.emailValidation.required('Required field'),
              phone: formValidation.phoneValidation,
              // relationshipManager: formValidation.inputTextValidation,
            })
          ),
        }),
      });

      try {
        schema.validateSync(values, { abortEarly: false });
        return {}; // No errors, return an empty object
      } catch (errors) {
        const errorObject = {};
        errors.inner.forEach((err) => {
          errorObject[err.path] = err.message;
        });
        return errorObject;
      }
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const apiPayload = {
          data: {
            executorHasCopy: values.executorHasCopy,
            additionalCopy: values.copyWithPerson,
            additionalCopyPerson: values.personName,
            additionalCopyPlace: values.location,
            bankLockerWillKept: values.copyInBankLocker,
            bankLockerNumber: values.bankLockerNo,
            branchName: values.branchName,
            branchAddress: values.branchAddress,
            bankName: values.bankName,
          },
          wealthManagerInfo: {
            hasWealthManager: values.wealthManager,
            managerData: values.wealthManagers,
          },
        };

        await generateWillApi(apiPayload);
        navigate('/willGenerationSuccess');
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false); // End loading
      }
    },
  });

  /**
   * Preview draft of the will by fetching existing data.
   * It updates the form values and sets the HTML content for the will preview.
   *
   * @async
   * @function previewDraft
   * @returns {Promise<void>} - A promise that resolves when the draft preview is fetched and form values are set.
   */
  const previewDraft = async () => {
    try {
      const response = await previewDraftApi();
      const lastWill = response.data?.lastWill;

      if (lastWill?.has_wealth_manager) {
        formik.setFieldValue('wealthManager', lastWill.has_wealth_manager);
        formik.setFieldValue(
          'wealthManagers',
          lastWill.wealth_manager_details || [
            {
              name: '',
              clientId: '',
              email: '',
              phone: '',
              relationshipManager: '',
            },
          ]
        );
      }

      if (lastWill) {
        // Use setFieldValue for each form field individually
        formik.setFieldValue('executorHasCopy', lastWill.executor_copy || false);
        formik.setFieldValue('copyWithPerson', lastWill.additional_copy || false);
        formik.setFieldValue('personName', lastWill.additional_copy_person || '');
        formik.setFieldValue('location', lastWill.additional_copy_place || '');
        formik.setFieldValue('copyInBankLocker', lastWill.bank_locker_will_kept || false);
        formik.setFieldValue('bankName', lastWill.bank_name || '');
        formik.setFieldValue('branchName', lastWill.branch_name || '');
        formik.setFieldValue('bankLockerNo', lastWill.bank_locker_number || '');
        formik.setFieldValue('branchAddress', lastWill.branch_address || '');
      }
      setHtml(response.data.html);
      setResp(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch user data on component mount
  useEffect(() => {
    // if (user?.role === 'reviewer' && !!review) {
    //   axios.interceptors.request.use((config) => {
    //     config.headers['X-Review-Id'] = +review;
    //     return config;
    //   });
    // }
    previewDraft();
  }, [user]);

  // Fetch user profile on initial mount
  useEffect(() => {
    fetchUser();
  }, []);

  /**
   * Handle step click navigation
   *
   * @function handleStepClick
   * @param {number} step - The step number to navigate to.
   */
  const handleStepClick = (step) => {
    // if (step === 1 && !isPlanPurchased) {
    //   return; // Block navigation to step 2 until the plan is purchased
    // }
    setActiveStep(step);
  };

  /**
   * Navigate to the plan purchase page.
   *
   * @function handlePlanPurchase
   */
  const handlePlanPurchase = () => {
    navigate('/Services');
  };

  /**
   * Handle checkbox change for formik values.
   *
   * @function handleCheckboxChange
   * @param {Object} event - The change event from the checkbox.
   */
  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    formik.setFieldValue(name, checked);
    if (name === 'copyWithPerson' && !checked) {
      formik.setFieldValue('personName', '');
      formik.setFieldValue('location', '');
    }
    if (name === 'copyInBankLocker' && !checked) {
      formik.setFieldValue('bankName', '');
      formik.setFieldValue('branchName', '');
      formik.setFieldValue('bankLockerNo', '');
      formik.setFieldValue('branchAddress', '');
    }
  };

  /**
   * Add a new wealth manager to the list.
   *
   * @function handleAddWealthManager
   */
  const handleAddWealthManager = () => {
    if (formik.values.wealthManagers.length < 3) {
      formik.setFieldValue('wealthManagers', [
        ...formik.values.wealthManagers,
        { name: '', clientId: '', email: '', phone: '', relationshipManager: '' },
      ]);
    }
  };

  /**
   * Delete a wealth manager from the list.
   *
   * @function handleDeleteWealthManager
   * @param {number} index - The index of the wealth manager to delete.
   */
  const handleDeleteWealthManager = (index) => {
    const updatedWealthManagers = formik.values.wealthManagers.filter((_, i) => i !== index);
    formik.setFieldValue('wealthManagers', updatedWealthManagers);
  };

  /**
   * Handle checkbox change for wealth manager checkbox.
   *
   * @function handleWealthManagerCheckboxChange
   * @param {Object} event - The change event from the checkbox.
   */
  const handleWealthManagerCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    formik.setFieldValue('wealthManager', isChecked);
    // If unchecked, reset wealth managers
    if (!isChecked) {
      formik.setFieldValue('wealthManagers', [{}]);
    }
  };

  /**
   * Check if the user has subscribed to the service.
   *
   * @function subscribed
   * @returns {boolean} - `true` if the user is subscribed, `false` otherwise.
   */
  const subscribed = useMemo(() => {
    // let consumed = user?.Subscriptions ?.length === 0;
    let consumed = true;
    user?.Subscriptions?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).forEach((sub) => {
      if (sub) {
        const userSub = sub.UserSubscription;
        if (userSub && userSub.SubscriptionId === 1) {
          // const today = new Date();
          // const subDay = new Date(userSub.createdAt);
          // const subDays = (today - subDay) / 86400 / 1000;
          // const allowed = sub.days;
          // if (subDays > allowed) consumed = true;
          // else consumed = false;
          if (userSub.active === true) consumed = false;
          else consumed = true;
        }
      }
    });
    return !consumed;
  }, [user]);

  /**
   * Pre-checks the user's data to verify if all necessary information is provided.
   *
   * @function prechecks
   * @returns {Object} - An object with the following keys:
   *  - `testator`: Array of missing testator details
   *  - `assets`: Boolean indicating if assets are provided
   *  - `distribution`: Array of distribution items
   *  - `beneficiaries`: Boolean indicating if beneficiaries are provided
   *  - `executors`: Boolean indicating if executors are provided
   *  - `witnesses`: Boolean indicating if witnesses are provided
   */
  const prechecks = useMemo(() => {
    const testator = ['first_name', 'last_name', 'pan', 'dob'].filter(
      (f) => !Object.keys(user?.profile ?? {}).includes(f)
    );
    const distribution = [
      ...(resp?.assets ?? []),
      {
        id: null,
        information: { caption: 'Any other assets not included in the Assets that need to be distributed' },
        type: '',
        sub_type: 'Residual Assets',
      },
    ]
      .map(({ id, name, information }) => {
        let mapped = true;
        [{ id: 0, deaths: [] }, ...(resp?.scenarios ?? [])].forEach((sc) => {
          const will = resp?.wills?.find((w) => w.PropertyId === id && sc.id === w.ScenarioId);
          if (!will) {
            mapped = false;
          } else {
            let value = 100;
            let total = 0;
            if (will?.distribution_type === 'value') {
              value =
                information?.value ??
                information?.amount ??
                information?.insured_amount ??
                information?.investment_amount ??
                information?.deposited_amount;
            } else if (will?.distribution_type === 'quantity') {
              value = information?.quantity ?? information?.qty ?? information?.number;
            }
            will?.mapping?.forEach((mapping) => {
              total += +(mapping.share ?? 0);
            });
            if (Math.abs(Number((value - total).toFixed(2))) <= 0.0) {
              // done 0.01 to 0.00
              total = value;
            }
            mapped = mapped && total === value;
          }
        });
        return {
          name:
            information?.name ??
            information?.description ??
            information?.description_or_name ??
            information?.description_of_vehicle,
          mapped,
        };
      })
      .filter((f) => !f.mapped);
    const assets = resp?.assets?.length > 0 ?? false;
    const beneficiaries = resp?.beneficiaries?.length > 0 ?? false;
    const executors = resp?.executors?.length > 0 ?? false;
    const witnesses = subscribed
      ? resp?.witnesses?.length === 2 ?? false
      : resp?.witnesses?.length === 2 || resp?.witnesses?.length === 1 || false;
    return { testator, assets, distribution, beneficiaries, executors, witnesses };
  }, [resp, user]);

  /**
   * Update the `step1Completed` state based on the precheck results.
   *
   * @function useEffect
   * @returns {void}
   */
  useEffect(() => {
    if (!prechecks) return;
    const { testator, assets, distribution, beneficiaries, executors, witnesses } = prechecks;
    // Check each step condition and update the step1Completed state accordingly
    setStep1Completed([
      testator.length === 0, // Step 1: Personal Details Filled
      beneficiaries && executors && witnesses, // Step 2: My People Added
      assets, // Step 3: Assets Added
      distribution.length === 0, // Step 4: Distribution Completed
    ]);
  }, [prechecks]);

  /**
   * Handle the redirection to different routes.
   *
   * @function handleRedirection
   * @param {string} route - The route to navigate to.
   */
  const handleRedirection = (route) => {
    navigate(route);
  };

  /**
   * Save form data and submit the form.
   *
   * @function handleSave
   * @returns {void}
   */
  const handleSave = () => {
    console.log('formik.values ', formik.values);
    console.log('formik.errors ', formik.errors);
    formik.handleSubmit();
  };

  /**
   * Determine whether the "Generate" button should be disabled.
   *
   * @function isGenerateButtonDisabled
   * @returns {boolean} - `true` if the button should be disabled, `false` otherwise.
   */
  const isGenerateButtonDisabled = useMemo(() => {
    const [personalDetailsFilled, myPeopleAdded, assetsAdded, distributionCompleted] = step1Completed;
    const hasCopyWithPerson = formik.values.copyWithPerson;
    const requiredFields = {
      personName: hasCopyWithPerson && !formik.values.personName,
      location: hasCopyWithPerson && !formik.values.location,
    };
    const hasCopyInBankLocker = formik.values.copyInBankLocker;
    const requiredFieldsForBank = {
      bankName: hasCopyInBankLocker && !formik.values.bankName,
      branchName: hasCopyInBankLocker && !formik.values.branchName,
      bankLockerNo: hasCopyInBankLocker && !formik.values.bankLockerNo,
      branchAddress: hasCopyInBankLocker && !formik.values.branchAddress,
    };

    const hasWealthManager = formik.values.wealthManager;
    const isWealthManagerFormValid = () => {
      if (hasWealthManager) {
        const requiredFieldsFilled = formik.values.wealthManagers.every(
          (manager) => manager.name && manager.phone && manager.email && manager.clientId
        );
        return requiredFieldsFilled;
      }
      return true;
    };
    const requiredFieldsIncomplete =
      Object.values(requiredFields).some(Boolean) || Object.values(requiredFieldsForBank).some(Boolean);
    const hasFormikErrors = Object.keys(formik.errors).length > 0;
    // Check all conditions for button enablement
    return (
      !(
        personalDetailsFilled &&
        myPeopleAdded &&
        assetsAdded &&
        distributionCompleted &&
        subscribed &&
        isWealthManagerFormValid()
      ) ||
      requiredFieldsIncomplete ||
      hasFormikErrors
    );
  }, [step1Completed, formik]);

  return (
    <Box sx={{ marginLeft: '32px', paddingRight: '80px' }}>
      <Typography variant="heading1" gutterBottom color={theme.palette.secondary.main}>
        Will Generation Checklist
      </Typography>
      <Stepper activeStep={activeStep} orientation="vertical" connector={<CustomStepConnector />}>
        <Step onClick={() => handleStepClick(0)}>
          <StepLabel StepIconComponent={CustomStepIcon} sx={{ marginLeft: '-4px' }}>
            <Typography variant="heading2">Checklist</Typography>
          </StepLabel>
          <StepContent
            sx={{
              borderLeft: activeStep === 0 ? `1px solid #FFD5B5` : theme.palette.primary.main,
            }}
          >
            <Stepper orientation="vertical" connector={<CustomInnerStepConnector />}>
              {[
                { label: 'Personal details filled', route: '/profilePage' },
                { label: 'My People added', route: '/myPeople' },
                { label: 'Assets added', route: '/assets' },
                { label: 'Distribution completed', route: '/distribution' },
              ].map(({ label, route }, index) => (
                <Step key={label} active={step1Completed[index]} completed={step1Completed[index]}>
                  <StepLabel StepIconComponent={CustomInnerStepIcon}>
                    <Typography
                      onClick={() => handleRedirection(route)}
                      variant="smallText"
                      sx={{
                        '&:hover': {
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </StepContent>
        </Step>

        <Step onClick={() => handleStepClick(1)}>
          <StepLabel StepIconComponent={CustomStepIcon} sx={{ marginLeft: '-4px' }}>
            {subscribed ? (
              <Typography variant="heading2" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                Add to Cart
                <Icon name="purchase-plan-icon.png" size={20} />
              </Typography>
            ) : (
              <ContainedButton1
                onClick={handlePlanPurchase}
                sx={{ padding: '14px 20px', textTransform: 'none', maxHeight: '40px' }}
              >
                <Typography variant="bodyText3">Add to Cart</Typography>
              </ContainedButton1>
            )}
          </StepLabel>
        </Step>

        <Step onClick={() => handleStepClick(2)}>
          <StepLabel StepIconComponent={CustomStepIcon} sx={{ marginLeft: '-4px' }}>
            <Typography variant="heading2">Will Custody Log</Typography>
          </StepLabel>
          {activeStep === 2 && (
            <StepContent
              sx={{
                borderLeft: activeStep === 2 ? `1px solid #FFD5B5` : theme.palette.primary.main,
              }}
            >
              <Box component={'form'}>
                {willCustodyOptions.map(({ name, label, textFields }) => (
                  <Box key={name}>
                    <Box sx={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
                      <Checkbox
                        checked={formik.values[name]}
                        onChange={handleCheckboxChange}
                        name={name}
                        icon={<Icon name="orange-checkbox-default.png" size={15} />}
                        checkedIcon={<Icon name="orange-checkbox-checked.svg" size={15} />}
                        sx={{ padding: 0 }}
                      />
                      <Typography variant="smallText">{label}</Typography>
                    </Box>
                    <Box>
                      {formik.values[name] && (
                        <Grid container md={10} lg={10} spacing={1} columnSpacing={2}>
                          {textFields.map((field, index) => (
                            <Grid
                              item
                              xs={12}
                              sm={12}
                              md={field.name === 'bankName' ? 12 : 6}
                              lg={field.name === 'bankName' ? 12 : 6}
                              key={index}
                            >
                              {field.name === 'bankName' ? (
                                <CustomAutocomplete
                                  name={field.name}
                                  type={'dropDown'}
                                  value={formik.values[field.name]}
                                  placeholder={field.label}
                                  handleChange={formik.handleChange}
                                  options={BankList}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                                  helperText={formik.touched[field.name] && formik.errors[field.name]}
                                  fullWidth
                                  sx={{
                                    '& .MuiInputBase-root': {
                                      height: '30px !important',
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '0px 10px !important',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                      fontSize: '14px',
                                      padding: 0,
                                      margin: 0,
                                      lineHeight: '30px',
                                    },
                                    '& .MuiAutocomplete-input': {
                                      padding: '0 8px !important',
                                      display: 'flex',
                                      alignItems: 'center',
                                    },
                                    '& .MuiAutocomplete-endAdornment': {
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'flex-end',
                                    },
                                    '& .MuiAutocomplete-inputRoot': {
                                      paddingRight: '12px !important',
                                    },
                                  }}
                                />
                              ) : (
                                <CustomInputField
                                  fullWidth
                                  placeholder={field.label}
                                  name={field.name}
                                  value={formik.values[field.name]}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                                  helperText={formik.touched[field.name] && formik.errors[field.name]}
                                />
                              )}
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                    <Divider sx={{ borderStyle: 'dashed', my: 2 }} variant="fullWidth" orientation="horizontal" />
                  </Box>
                ))}
                <Box>
                  <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center', mb: '16px' }}>
                    <Checkbox
                      checked={formik.values.wealthManager}
                      onChange={handleWealthManagerCheckboxChange} // Updated handler
                      name="wealthManager"
                      icon={<Icon name="orange-checkbox-default.png" size={15} />}
                      checkedIcon={<Icon name="orange-checkbox-checked.svg" size={15} />}
                      sx={{ padding: 0 }}
                    />
                    <Typography variant="smallText">Add Wealth Manager(s)</Typography>
                    {formik?.values?.wealthManager && formik?.values?.wealthManagers?.length < 3 && (
                      <Chip
                        onClick={handleAddWealthManager}
                        label={<Typography variant="suggestiveText">Add +</Typography>}
                        sx={{ backgroundColor: '#F5F5F5', height: '19px', p: '8px 0' }}
                      />
                    )}
                  </Box>
                  {formik.values.wealthManager && (
                    <Box>
                      {formik?.values?.wealthManagers?.map((manager, index) => (
                        <Box key={index} sx={{ marginBottom: 2 }}>
                          <Grid container spacing={2}>
                            <Grid item md={5}>
                              <CustomInputField
                                placeholder="Name*"
                                name={`wealthManagers.${index}.name`}
                                value={manager.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  Boolean(formik?.touched?.wealthManagers?.[index]?.name) &&
                                  Boolean(formik.errors[`wealthManagers[${index}].name`])
                                }
                                helperText={
                                  formik?.touched?.wealthManagers?.[index]?.name &&
                                  formik.errors[`wealthManagers[${index}].name`]
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid item md={5}>
                              <CustomInputField
                                placeholder="Client ID*"
                                name={`wealthManagers.${index}.clientId`}
                                value={manager.clientId}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  formik.touched.wealthManagers?.[index]?.clientId &&
                                  Boolean(formik.errors[`wealthManagers[${index}].clientId`])
                                }
                                helperText={
                                  formik.touched.wealthManagers?.[index]?.clientId &&
                                  formik.errors[`wealthManagers[${index}].clientId`]
                                }
                                fullWidth
                              />
                            </Grid>
                            {formik.values.wealthManagers.length > 1 && (
                              <Grid item md={2}>
                                <IconButton onClick={() => handleDeleteWealthManager(index)}>
                                  <Icon name="delete-icon-pink.svg" size={20} />
                                </IconButton>
                              </Grid>
                            )}
                            <Grid item md={5}>
                              <CustomInputField
                                placeholder="Email ID*"
                                name={`wealthManagers.${index}.email`}
                                value={manager.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  formik.touched.wealthManagers?.[index]?.email &&
                                  Boolean(formik.errors[`wealthManagers[${index}].email`])
                                }
                                helperText={
                                  formik.touched.wealthManagers?.[index]?.email &&
                                  formik.errors[`wealthManagers[${index}].email`]
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid item md={5}>
                              <PhoneField
                                name={`wealthManagers.${index}.phone`}
                                value={manager.phone}
                                handleChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  formik.touched.wealthManagers?.[index]?.phone &&
                                  Boolean(formik.errors[`wealthManagers[${index}].phone`])
                                }
                                helperText={
                                  formik.touched.wealthManagers?.[index]?.phone &&
                                  formik.errors[`wealthManagers[${index}].phone`]
                                }
                                placeholder="Mobile Number*"
                                disabled={false}
                                sx={{
                                  '& .MuiInputBase-root': {
                                    height: '30px',
                                  },
                                  '& .MuiInputBase-input::placeholder': {
                                    fontSize: '14px',
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item md={5}>
                              <CustomInputField
                                placeholder="Relationship Manager"
                                name={`wealthManagers.${index}.relationshipManager`}
                                value={manager.relationshipManager}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  formik.touched.wealthManagers?.[index]?.relationshipManager &&
                                  Boolean(formik.errors[`wealthManagers[${index}].relationshipManager`])
                                }
                                helperText={
                                  formik.touched.wealthManagers?.[index]?.relationshipManager &&
                                  formik.errors[`wealthManagers[${index}].relationshipManager`]
                                }
                                fullWidth
                              />
                              {/* console.log('formik.errors ', formik.errors["wealthManagers[0].name"]); */}
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </StepContent>
          )}
        </Step>

        <Step>
          <StepLabel StepIconComponent={CustomStepIcon} sx={{ marginLeft: '-4px' }}>
            <ContainedButton1
              variant="contained"
              color="primary"
              sx={{ height: '40px', padding: '0 8px' }}
              onClick={handleSave}
              disabled={isLoading || isGenerateButtonDisabled}
            >
              <Typography variant="bodyText3" color={theme.palette.common.white}>
                Generate Will
              </Typography>
            </ContainedButton1>
          </StepLabel>
        </Step>
      </Stepper>
    </Box>
  );
};

export default WillGenerationChecklist;

const willCustodyOptions = [
  {
    name: 'executorHasCopy',
    label: 'Each executor has a copy of this Will',
    textFields: [],
  },
  {
    name: 'copyWithPerson',
    label: 'A copy of this Will is also with this person',
    textFields: [
      { label: 'Person Name*', name: 'personName' },
      { label: 'Location*', name: 'location' },
    ],
  },
  {
    name: 'copyInBankLocker',
    label: 'A copy of this Will is also kept in a bank locker',
    textFields: [
      { label: 'Bank Name*', name: 'bankName' },
      { label: 'Branch Name*', name: 'branchName' },
      { label: 'Bank Locker No.*', name: 'bankLockerNo' },
      { label: 'Branch Address*', name: 'branchAddress' },
    ],
  },
];
